import React, { useState, useEffect } from 'react';
import { 
  Languages, Loader2, Search, Download
} from 'lucide-react';
import { languageAPI, groupsAPI } from '../services/api';
import Pagination from '../components/Pagination';
import * as XLSX from 'xlsx';

const LANGUAGES = {
  russian: { name: 'Rus tili', flag: '🇷🇺', color: '#dc2626' },
  english: { name: 'Ingliz tili', flag: '🇬🇧', color: '#2563eb' },
  german: { name: 'Nemis tili', flag: '🇩🇪', color: '#ca8a04' },
};

const LanguagePage = () => {
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;
  const [filterLanguage, setFilterLanguage] = useState('');

  useEffect(() => {
    fetchGroups();
    fetchResults();
  }, []);

  useEffect(() => {
    fetchResults();
  }, [filterGroup, filterLanguage, currentPage]);

  const fetchGroups = async () => {
    try {
      const response = await groupsAPI.getAll();
      setGroups(response.data.results || response.data || []);
    } catch (error) {
      console.error('Guruhlarni yuklashda xato:', error);
    }
  };

  const fetchResults = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage };
      if (filterGroup) params.group = filterGroup;
      if (filterLanguage) params.language = filterLanguage;
      if (searchTerm) params.search = searchTerm;
      
      const response = await languageAPI.getResults(params);
      const data = response.data;
      if (data.results) {
        setResults(data.results);
        setTotalCount(data.count || data.results.length);
      } else {
        setResults([]);
        setTotalCount(0);
      }
      setStats(response.data.stats || null);
    } catch (error) {
      console.error('Natijalarni yuklashda xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchResults();
  };

  const exportToExcel = () => {
    const data = results.map(r => ({
      'Talaba ID': r.student_id,
      'F.I.Sh': r.student_name,
      'Guruh': r.group_name || '-',
      'Til': r.language_display,
      "Tushib qolgan so'z": `${r.fill_blank_score}/${r.fill_blank_total}`,
      'Tarjima tanlash': `${r.choose_score}/${r.choose_total}`,
      'Tarjima yozish': `${r.write_score}/${r.write_total}`,
      'Jami ball': `${r.total_score}/${r.total_questions}`,
      'Foiz': `${r.percentage}%`,
      'Daraja': r.level,
      'Sana': new Date(r.completed_at).toLocaleDateString('uz-UZ')
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Til testlari');
    XLSX.writeFile(wb, 'til_test_natijalari.xlsx');
  };

  const filteredResults = results.filter(r => {
    const name = r.student_name?.toLowerCase() || '';
    const id = r.student_id?.toLowerCase() || '';
    const term = searchTerm.toLowerCase();
    return name.includes(term) || id.includes(term);
  });

  if (loading && results.length === 0) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Languages size={28} /> Til bilish test natijalari</h1>
          <p>Talabalar til testi natijalari</p>
        </div>
        <button className="btn btn-success" onClick={exportToExcel} disabled={results.length === 0}>
          <Download size={18} />
          Excel export
        </button>
      </header>

      {/* Statistika */}
      {stats && (
        <div className="language-stats">
          <div className="lang-stat-card total">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Jami natijalar</span>
          </div>
          {Object.entries(LANGUAGES).map(([key, info]) => (
            <div key={key} className="lang-stat-card" style={{ '--lang-color': info.color }}>
              <span className="lang-flag">{info.flag}</span>
              <div>
                <span className="stat-value">{stats[key]}</span>
                <span className="stat-label">{info.name}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filterlar */}
      <div className="filters-row">
        <div className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="Talabani qidirish..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <select 
          className="filter-select"
          value={filterGroup}
          onChange={(e) => setFilterGroup(e.target.value)}
        >
          <option value="">Barcha guruhlar</option>
          {groups.map(g => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </select>
        <select 
          className="filter-select"
          value={filterLanguage}
          onChange={(e) => setFilterLanguage(e.target.value)}
        >
          <option value="">Barcha tillar</option>
          {Object.entries(LANGUAGES).map(([key, info]) => (
            <option key={key} value={key}>{info.name}</option>
          ))}
        </select>
      </div>

      {/* Jadval */}
      {filteredResults.length === 0 ? (
        <div className="empty-state">
          <Languages size={48} />
          <p>Natijalar topilmadi</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{width: '50px'}}>№</th>
                  <th>Talaba</th>
                  <th>Guruh</th>
                  <th>Til</th>
                  <th style={{textAlign: 'center'}}>Ball</th>
                  <th style={{textAlign: 'center'}}>Foiz</th>
                  <th>Daraja</th>
                  <th>Sana</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((result, idx) => {
                  const langInfo = LANGUAGES[result.language];
                  return (
                    <tr key={result.id}>
                      <td style={{color: '#94a3b8', textAlign: 'center'}}>{idx + 1}</td>
                      <td>
                        <div className="student-info-cell">
                          <strong>{result.student_name}</strong>
                          <span className="student-id-small">{result.student_id}</span>
                        </div>
                      </td>
                      <td><span className="group-badge-small">{result.group_name || '-'}</span></td>
                      <td>
                        <span className="language-badge" style={{ background: langInfo?.color }}>
                          {langInfo?.flag} {result.language_display}
                        </span>
                      </td>
                      <td style={{textAlign: 'center', fontWeight: 600}}>
                        {result.total_score}/{result.total_questions}
                      </td>
                      <td style={{textAlign: 'center'}}>
                        <span className={`percentage-badge ${result.percentage >= 80 ? 'high' : result.percentage >= 50 ? 'medium' : 'low'}`}>
                          {result.percentage}%
                        </span>
                      </td>
                      <td>
                        <span className="level-badge">{result.level}</span>
                      </td>
                      <td style={{color: '#64748b', fontSize: '13px'}}>
                        {new Date(result.completed_at).toLocaleDateString('uz-UZ')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguagePage;
